import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTheme, makeStyles } from '@material-ui/core/styles';
import { Grid, Divider, Typography, TextField, Tooltip } from '@material-ui/core';
import { ToggleButtonGroup } from '@material-ui/lab';
import { InfoOutlined } from '@material-ui/icons';
import AdvancedSettingsFooter from '../../../components/KillshotGroups/AdvancedSettings/footer';
import { NormalStyledDarkToggleButton, SplitStyledToggleButton } from '../../../components/Common/ToggleButton';

const styles = makeStyles({
  toggleButtons: {
    width: '43px',
    height: '39px',
    fontSize: '14px',
    fontWeight: '400',
    textTransform: 'none',
  },
  toggleFirstButton: {
    width: '54px',
    height: '39px',
    fontSize: '14px',
    fontWeight: '400',
    textTransform: 'none',
  },
  sgToggleButtons: {
    width: '43px',
    height: '39px',
    fontSize: '14px',
    fontWeight: '400',
    textTransform: 'none',
  },
  sgToggleFirstButton: {
    width: '54px',
    height: '39px',
    fontSize: '14px',
    fontWeight: '400',
    textTransform: 'none',
  },
  sgToggleCustomButton: {
    minWidth: '60px',
    height: '39px',
    fontSize: '13px',
    fontWeight: '400',
    textTransform: 'none',
    padding: '0 8px',
  },
  textStyle: {
    fontSize: '14px',
    fontWeight: '400',
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    marginTop: '16px',
  },
  customInput: {
    width: '80px',
  },
  infoIcon: {
    fontSize: '18px',
    color: '#9e9e9e',
    marginLeft: '4px',
    cursor: 'pointer',
    verticalAlign: 'middle',
  },
});

// Sentinel value for the Custom toggle button. Must be numeric (for ToggleButtonGroup)
// and not match any preset discount value. Never sent to the BE.
const SG_CUSTOM_TOGGLE_VALUE = -1;

const GroupAdvanceDiscounting = props => {
  const { handleMarketModalClose, handleMultipleAttrEdit, pricingGroup } = props;
  const theme = useTheme();
  const classes = styles();

  const gtDiscountOptions = [
    { label: 'None', value: 0 },
    { label: '10%', value: 10 },
    { label: '11%', value: 11 },
    { label: '12%', value: 12 },
    { label: '13%', value: 13 },
    { label: '14%', value: 14 },
    { label: '15%', value: 15 },
  ];

  const sgDiscountOptions = [
    { label: 'None', value: 0 },
    { label: '2.5%', value: 2.5 },
    { label: '5%', value: 5 },
    { label: '7.5%', value: 7.5 },
    { label: '10%', value: 10 },
    { label: '12.5%', value: 12.5 },
    { label: '15%', value: 15 },
    { label: 'Custom', value: SG_CUSTOM_TOGGLE_VALUE },
  ];

  const sgPresetValues = sgDiscountOptions.filter(opt => opt.value !== SG_CUSTOM_TOGGLE_VALUE).map(opt => opt.value);

  const [discountValue, setDiscountValue] = useState(0);
  const [sgDiscountValue, setSgDiscountValue] = useState(0);
  const [sgCustomValue, setSgCustomValue] = useState('');
  const [sgIsCustom, setSgIsCustom] = useState(false);
  const [sgValidationError, setSgValidationError] = useState('');

  const validateSgCustom = value => {
    if (value === '' || value === null || value === undefined) {
      return '';
    }
    const num = parseFloat(value);
    if (Number.isNaN(num)) {
      return 'Invalid number';
    }
    if (num < 2.5) {
      return 'Minimum is 2.5%';
    }
    if (num > 15) {
      return 'Maximum is 15%';
    }
    const decimalParts = String(value).split('.');
    if (decimalParts.length > 1 && decimalParts[1].length > 1) {
      return 'One decimal place only';
    }
    return '';
  };

  const handleModalClose = () => {
    handleMarketModalClose();
  };

  const handleSetCriteria = () => {
    let finalSgDiscount = 0;
    if (sgIsCustom) {
      finalSgDiscount = sgCustomValue !== '' ? parseFloat(sgCustomValue) : 0;
    } else {
      finalSgDiscount = sgDiscountValue;
    }
    handleMultipleAttrEdit({ discount: discountValue, sg_discount: finalSgDiscount });
    handleMarketModalClose();
  };

  const handleGtOnChange = (event, newDiscount) => {
    if (newDiscount === null) {
      return;
    }
    setDiscountValue(newDiscount);
  };

  const handleSgOnChange = (event, newDiscount) => {
    if (newDiscount === null) {
      return;
    }
    if (newDiscount === SG_CUSTOM_TOGGLE_VALUE) {
      setSgIsCustom(true);
    } else {
      setSgIsCustom(false);
      setSgCustomValue('');
      setSgValidationError('');
      setSgDiscountValue(newDiscount);
    }
  };

  const handleSgCustomChange = event => {
    const val = event.target.value;
    // Only allow digits, one decimal point, and max one decimal place
    if (val !== '' && !/^\d*\.?\d?$/.test(val)) {
      return;
    }
    setSgCustomValue(val);
    setSgValidationError(validateSgCustom(val));
  };

  const isSetCriteriaDisabled = sgIsCustom && sgValidationError !== '';

  useEffect(() => {
    if (pricingGroup.discount) {
      setDiscountValue(Number(pricingGroup.discount));
    }
  }, [pricingGroup.discount]);

  useEffect(() => {
    if (pricingGroup.sg_discount) {
      const val = Number(pricingGroup.sg_discount);
      if (sgPresetValues.includes(val)) {
        setSgDiscountValue(val);
        setSgIsCustom(false);
      } else {
        setSgIsCustom(true);
        setSgCustomValue(String(val));
      }
    }
  }, [pricingGroup.sg_discount]);

  const renderToggleButton = (item, isFirst, variant = 'gt') => {
    let className;
    if (variant === 'sg') {
      if (item.value === SG_CUSTOM_TOGGLE_VALUE) {
        className = classes.sgToggleCustomButton;
      } else {
        className = isFirst ? classes.sgToggleFirstButton : classes.sgToggleButtons;
      }
    } else {
      className = isFirst ? classes.toggleFirstButton : classes.toggleButtons;
    }
    return theme.palette.type === 'dark' ? (
      <NormalStyledDarkToggleButton className={className} value={item.value} key={item.value}>
        {item.label}
      </NormalStyledDarkToggleButton>
    ) : (
      <SplitStyledToggleButton className={className} value={item.value} key={item.value}>
        {item.label}
      </SplitStyledToggleButton>
    );
  };

  return (
    <Grid container direction="column">
      <Grid item style={{ padding: '24px 24px 0px 24px' }}>
        <Grid container direction="column" spacing={1} style={{ minHeight: '530px' }}>
          {/* GameTime Discount Section */}
          <Grid item>
            <Typography className={classes.textStyle}>Please select the GameTime discount.</Typography>
          </Grid>
          <Grid item style={{ marginTop: '5px' }}>
            <ToggleButtonGroup value={discountValue} onChange={handleGtOnChange} exclusive style={{ width: '283px' }}>
              {gtDiscountOptions.map((item, idx) => renderToggleButton(item, idx === 0))}
            </ToggleButtonGroup>
          </Grid>

          {/* SeatGeek Discount Section */}
          <Grid item>
            <Typography className={classes.sectionTitle}>Please select the SeatGeek discount.</Typography>
          </Grid>
          <Grid item style={{ marginTop: '5px' }}>
            <ToggleButtonGroup
              value={sgIsCustom ? SG_CUSTOM_TOGGLE_VALUE : sgDiscountValue}
              onChange={handleSgOnChange}
              exclusive
            >
              {sgDiscountOptions.map((item, idx) => renderToggleButton(item, idx === 0, 'sg'))}
            </ToggleButtonGroup>
          </Grid>
          {sgIsCustom && (
            <Grid item>
              <Grid container alignItems="center">
                <Grid item>
                  <TextField
                    className={classes.customInput}
                    size="small"
                    variant="outlined"
                    placeholder="%"
                    value={sgCustomValue}
                    onChange={handleSgCustomChange}
                    error={sgValidationError !== ''}
                    helperText={sgValidationError}
                    inputProps={{ inputMode: 'decimal' }}
                  />
                </Grid>
                <Grid item>
                  <Tooltip
                    title="Discounts must be at least 2.5% or $1 to apply. Values below this are ignored, and discounts above 15% are capped at 15%."
                    arrow
                  >
                    <InfoOutlined className={classes.infoIcon} />
                  </Tooltip>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item style={{ padding: '0px 24px 0px 24px' }}>
        <Grid container className={classes.footerMessage}>
          <Grid item xs={12}>
            <Typography className={classes.textStyle}>
              Please make sure to save the group after you SET CRITERIA in order for your selections to be saved.
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      <Divider style={{ width: '100%', color: '#E9E9E9' }} />
      <Grid item style={{ padding: '0px 24px 24px 24px' }}>
        <AdvancedSettingsFooter
          handleModalClose={handleModalClose}
          handleSetCriteria={handleSetCriteria}
          disabled={isSetCriteriaDisabled}
        />
      </Grid>
    </Grid>
  );
};

GroupAdvanceDiscounting.propTypes = {
  handleMarketModalClose: PropTypes.func.isRequired,
  handleMultipleAttrEdit: PropTypes.func.isRequired,
  pricingGroup: PropTypes.object.isRequired,
};

export default GroupAdvanceDiscounting;
